import PageScaffold from '../../component/pageScaffold/PageScaffold';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import InputAdornment from '@mui/material/InputAdornment';
import { useTheme } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { getEnterpriseIdFromToken } from '../../component/context/EnterpriseConfigContext';
import DefaultTable from '../../component/table/DefaultTable';
import DesktopPeriodSelector from '../../component/common/DesktopPeriodSelector';
import 'dayjs/locale/pt-br';
import { httpClient } from '../../common/httpclient';
import { useNavigate, useNavigationType } from 'react-router-dom';
import {
    BarChart3,
    Ban,
    Eye,
    Play,
    Search,
    Undo2,
    X,
} from 'lucide-react';
import FilterToolbar, { FilterChips } from '../../component/filter/FilterToolbar';
import DialogForm from '../../component/dialog/DialogForm';
import StatusChip from '../../component/common/StatusChip';
import NewItemButton from '../../component/common/NewItemButton';
import { cancelSale, getSaleRowGrossAmount, getSaleRowItemDiscount, getSaleRowFinalTotal, hasNonZeroProductItemDiscount } from '../../common/saleUtils';
import usePermissions from '../../hooks/usePermissions';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import MobileSalesList from '../../component/sale/MobileSalesList';

// Rótulos exibidos no filtro de status / chip removível — mesmos 4 valores
// reais de status de venda usados por `sales/View.js` e `StatusChip
// domain="sale"` (DESIGN-SYSTEM.md §4 + prototipo-visualizacao-venda.html).
const STATUS_OPTIONS = ['Iniciada', 'Finalizada', 'Cancelada', 'Estornada'];

// Aplica os filtros de status + busca (client-side, sobre o conjunto já
// carregado do período ativo) — mesma técnica que já existia para o filtro
// de status, agora também usada pela busca por número da venda (toolbar do
// prototipo-lista-vendas.html: período+status+busca).
// Converte "1.234,56" (formato pt-BR retornado pela API/helpers de exibição)
// para número, e formata de volta — usado só para somar o valor bruto
// ("Total de vendas") exibido nos chips a partir das linhas já carregadas,
// sem precisar de um novo campo/endpoint no backend.
function parseBRMoney(value) {
    if (value === undefined || value === null || value === '') return 0;
    const n = parseFloat(String(value).replace(/\./g, '').replace(',', '.'));
    return Number.isNaN(n) ? 0 : n;
}

function formatBRMoney(value) {
    return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Snapshot dos filtros aplicados, gravado em sessionStorage (chaveado por
// empresa) SOMENTE ao abrir o detalhe de uma venda e consumido (removido) na
// montagem seguinte da lista. Restaurado apenas se a volta for um POP.
// Também removido no logout (AccountMenu).
export const SALES_FILTERS_STORAGE_PREFIX = 'sales_list_filters:';

function salesFiltersKey() {
    return `${SALES_FILTERS_STORAGE_PREFIX}${getEnterpriseIdFromToken() || 'anon'}`;
}

// Período padrão da listagem: do primeiro ao último dia do mês atual (fuso
// local — dayjs() opera no fuso do navegador e a API recebe só DD/MM/YYYY).
function getCurrentMonthRange() {
    const now = dayjs();
    return { start: now.startOf('month'), end: now.endOf('month').startOf('day') };
}

function loadSavedSalesFilters(allowRestore) {
    const empty = { ...getCurrentMonthRange(), status: '', search: '', page: 0, pageSize: 8 };
    if (!allowRestore) return empty;
    try {
        const raw = sessionStorage.getItem(salesFiltersKey());
        if (!raw) return empty;
        const saved = JSON.parse(raw);
        if (!saved || typeof saved !== 'object') return empty;
        const parseDate = (v) => {
            if (typeof v !== 'string') return null;
            const d = dayjs(v, 'YYYY-MM-DD', true);
            return d.isValid() ? d : null;
        };
        const pageSizeOptions = [5, 8, 15, 25, 50];
        return {
            start: parseDate(saved.start),
            end: parseDate(saved.end),
            status: STATUS_OPTIONS.includes(saved.status) ? saved.status : '',
            search: typeof saved.search === 'string' ? saved.search : '',
            page: Number.isInteger(saved.page) && saved.page >= 0 ? saved.page : 0,
            pageSize: pageSizeOptions.includes(saved.pageSize) ? saved.pageSize : 8,
        };
    } catch (error) {
        return empty;
    }
}

// Filtro de data (período aplicado) persistente em localStorage, chaveado por
// empresa. Sobrevive a navegação, F5 e novo login; só é removido ao clicar em
// "Limpar".
const SALES_DATE_FILTER_STORAGE_PREFIX = 'sales_list_date_filter:';

function salesDateFilterKey() {
    return `${SALES_DATE_FILTER_STORAGE_PREFIX}${getEnterpriseIdFromToken() || 'anon'}`;
}

function loadPersistedSalesDateFilter() {
    try {
        const raw = localStorage.getItem(salesDateFilterKey());
        if (!raw) return null;
        const saved = JSON.parse(raw);
        if (!saved || typeof saved !== 'object') return null;
        const parseDate = (v) => {
            if (typeof v !== 'string') return null;
            const d = dayjs(v, 'YYYY-MM-DD', true);
            return d.isValid() ? d : null;
        };
        const start = parseDate(saved.start);
        const end = parseDate(saved.end);
        if (!start && !end) return null;
        return { start, end };
    } catch (error) {
        return null;
    }
}

function persistSalesDateFilter(start, end) {
    try {
        const hasStart = start && start.isValid();
        const hasEnd = end && end.isValid();
        if (!hasStart && !hasEnd) {
            localStorage.removeItem(salesDateFilterKey());
            return;
        }
        localStorage.setItem(salesDateFilterKey(), JSON.stringify({
            start: hasStart ? start.format('YYYY-MM-DD') : null,
            end: hasEnd ? end.format('YYYY-MM-DD') : null,
        }));
    } catch (error) {
        // localStorage indisponível: apenas não persiste o filtro de data.
    }
}

export default function List() {
    const theme = useTheme();
    // Lido uma única vez, antes da primeira requisição.
    // Só restaura quando a entrada na lista é um retorno (POP: botão voltar da
    // tela via navigate(-1) ou do navegador); menu/links (PUSH) abrem vazia.
    const navigationType = useNavigationType();
    const [initialFilters] = useState(() => {
        const filters = loadSavedSalesFilters(navigationType === 'POP');
        // O filtro de data persistido prevalece, em qualquer tipo de navegação.
        const persistedDates = loadPersistedSalesDateFilter();
        return persistedDates ? { ...filters, start: persistedDates.start, end: persistedDates.end } : filters;
    });
    const [search, setSearch] = useState(initialFilters.search);
    // Busca por número com debounce — evita uma requisição por tecla.
    const [debouncedSearch, setDebouncedSearch] = useState(initialFilters.search.trim());
    const previousDebouncedSearch = useRef(initialFilters.search.trim());
    const [loading, setLoading] = useState(true);
    // Filtro de data inicia com o mês atual (sem filtro salvo). Se o usuário
    // esvaziar as datas e buscar, traz todas as vendas (mais recentes primeiro),
    // paginadas no servidor.
    const [selectedDate, setSelectedDate] = useState(initialFilters.start);
    const [selectedDateEnd, setSelectedDateEnd] = useState(initialFilters.end);
    // Período efetivamente aplicado na última busca — só muda quando o
    // usuário clica em "Buscar"/"Limpar", não a cada seleção no date picker.
    // Alimenta o chip "Período".
    const [appliedPeriod, setAppliedPeriod] = useState({ start: initialFilters.start, end: initialFilters.end });
    const [sales, setSales] = useState([]);
    const [selectedStatus, setSelectedStatus] = useState(initialFilters.status);
    const [amountTotal, setAmountTotal] = useState('0,00');
    // Paginação server-side (page é 0-based na tela; a API usa 1-based).
    const [page, setPage] = useState(initialFilters.page);
    const [pageSize, setPageSize] = useState(initialFilters.pageSize);
    const [totalCount, setTotalCount] = useState(0);
    const [reloadKey, setReloadKey] = useState(0);
    const [openDialogCancelSale, setOpenDialogCancelSale] = useState(false);
    const [selectedSaleForCancel, setSelectedSaleForCancel] = useState(null);
    const [cancelReason, setCancelReason] = useState('');
    const [loadingCancel, setLoadingCancel] = useState(false);
    const [openDialogReverseSale, setOpenDialogReverseSale] = useState(false);
    const [selectedSaleForReverse, setSelectedSaleForReverse] = useState(null);
    const [loadingReverse, setLoadingReverse] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const isMobileShell = useIsMobileShell();
    // Mobile: lista contínua ("Carregar mais") em vez de paginação; página própria para não
    // interferir na paginação do desktop/tablet.
    const MOBILE_PAGE_SIZE = 20;
    const [mobilePage, setMobilePage] = useState(0);

    // Debounce da busca; ao mudar o termo volta para a primeira página.
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
        return () => clearTimeout(timer);
    }, [search]);

    // Só volta para a primeira página se o termo realmente mudou (na montagem
    // o termo restaurado não deve zerar a página restaurada).
    useEffect(() => {
        if (previousDebouncedSearch.current === debouncedSearch) return;
        previousDebouncedSearch.current = debouncedSearch;
        setPage(0);
        setMobilePage(0);
    }, [debouncedSearch]);

    // Consome o snapshot após a montagem (restaurado ou não). Fica no efeito,
    // e não no inicializador do state, para o StrictMode (dupla invocação/
    // montagem em dev) não perder o snapshot; remover é idempotente.
    useEffect(() => {
        try {
            sessionStorage.removeItem(salesFiltersKey());
        } catch (error) {
            // sessionStorage indisponível: nada a limpar.
        }
    }, []);

    // Salva os filtros aplicados imediatamente antes de ir ao detalhe da venda.
    const saveFiltersAndOpenSale = (saleId) => {
        try {
            const hasStart = appliedPeriod.start && appliedPeriod.start.isValid();
            const hasEnd = appliedPeriod.end && appliedPeriod.end.isValid();
            sessionStorage.setItem(salesFiltersKey(), JSON.stringify({
                start: hasStart ? appliedPeriod.start.format('YYYY-MM-DD') : null,
                end: hasEnd ? appliedPeriod.end.format('YYYY-MM-DD') : null,
                status: selectedStatus,
                search: debouncedSearch,
                page,
                pageSize,
            }));
        } catch (error) {
            // sessionStorage indisponível: apenas não preserva os filtros.
        }
        navigate(`/sales/${saleId}`);
    };

    // Busca a página atual no servidor sempre que filtros/página/tamanho mudam.
    useEffect(() => {
        let cancelled = false;
        const requestPage = isMobileShell ? mobilePage : page;
        const params = { page: requestPage + 1, page_size: isMobileShell ? MOBILE_PAGE_SIZE : pageSize };
        if (appliedPeriod.start && appliedPeriod.start.isValid()) params.startDate = appliedPeriod.start.format('DD/MM/YYYY');
        if (appliedPeriod.end && appliedPeriod.end.isValid()) params.endDate = appliedPeriod.end.format('DD/MM/YYYY');
        if (selectedStatus) params.status = selectedStatus;
        if (debouncedSearch) params.search = debouncedSearch;

        setLoading(true);
        httpClient.get('/sales', { params })
            .then((response) => {
                if (cancelled) return;
                const received = response.data.sales || [];
                if (isMobileShell && requestPage > 0) {
                    setSales((prev) => [...prev, ...received.filter((row) => !prev.some((p) => p.id === row.id))]);
                } else {
                    setSales(received);
                }
                setAmountTotal(response.data.amount);
                setTotalCount(response.data.total || 0);
            })
            .catch((error) => {
                if (cancelled) return;
                console.error('Erro ao listar vendas:', error);
                setSales([]);
                setTotalCount(0);
                setSnackbarMessage(error?.response?.data?.message || 'Erro ao listar vendas.');
                setSnackbarSeverity('error');
                setOpenSnackbar(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [page, pageSize, appliedPeriod, selectedStatus, debouncedSearch, reloadKey, isMobileShell, mobilePage]);

    const handleChangeSearch = (searchValue) => {
        setSearch(searchValue);
    }

    const handleChangeStatus = (statusValue) => {
        setSelectedStatus(statusValue);
        setPage(0);
        setMobilePage(0);
    }

    const handleApplyFilters = () => {
        setAppliedPeriod({ start: selectedDate, end: selectedDateEnd });
        persistSalesDateFilter(selectedDate, selectedDateEnd);
        setPage(0);
        setMobilePage(0);
    }

    // Mobile: aplica o período escolhido na folha "Período" (datas explícitas, sem depender do estado).
    const handleApplyMobilePeriod = (start, end) => {
        setSelectedDate(start);
        setSelectedDateEnd(end);
        setAppliedPeriod({ start, end });
        persistSalesDateFilter(start, end);
        setPage(0);
        setMobilePage(0);
    }

    // Chip "Período" removível: volta ao período padrão (mês atual) e aplica na hora,
    // sem mexer em status/busca.
    const handleResetPeriod = () => {
        const defaultRange = getCurrentMonthRange();
        setSelectedDate(defaultRange.start);
        setSelectedDateEnd(defaultRange.end);
        setAppliedPeriod(defaultRange);
        persistSalesDateFilter(null, null);
        setPage(0);
        setMobilePage(0);
    }

    const handleClearFilters = () => {
        setSelectedStatus('');
        setSearch('');
        setDebouncedSearch('');
        // Volta ao período padrão (mês atual), sem período persistido.
        const defaultRange = getCurrentMonthRange();
        setSelectedDate(defaultRange.start);
        setSelectedDateEnd(defaultRange.end);
        setAppliedPeriod(defaultRange);
        persistSalesDateFilter(null, null);
        setPage(0);
        setMobilePage(0);
    }

    const handlePageSizeChange = (newSize) => {
        setPageSize(newSize);
        setPage(0);
    }

    const handleCancelSaleClick = (saleId) => {
        setSelectedSaleForCancel(saleId);
        setCancelReason('');
        setOpenDialogCancelSale(true);
    }

    const handleConfirmCancelSale = async () => {
        if (!cancelReason.trim()) {
            return;
        }

        setLoadingCancel(true);
        try {
            await cancelSale(selectedSaleForCancel, cancelReason);

            // Recarrega a página atual (status, total e contagem mudam)
            setReloadKey((k) => k + 1);

            // Fechar dialog e limpar estados
            setOpenDialogCancelSale(false);
            setSelectedSaleForCancel(null);
            setCancelReason('');

            setSnackbarMessage('Venda cancelada com sucesso!');
            setSnackbarSeverity('success');
            setOpenSnackbar(true);
        } catch (error) {
            console.error('Erro ao cancelar venda:', error);

            setSnackbarMessage(error?.response?.data?.message || 'Erro ao cancelar venda.');
            setSnackbarSeverity('error');
            setOpenSnackbar(true);
        } finally {
            setLoadingCancel(false);
        }
    }

    const handleReverseSaleClick = (saleId) => {
        setSelectedSaleForReverse(saleId);
        setOpenDialogReverseSale(true);
    }

    const handleConfirmReverseSale = async () => {
        setLoadingReverse(true);
        try {
            await httpClient.post(`/sales/reverse/${selectedSaleForReverse}`);

            // Recarrega a página atual (status, total e contagem mudam)
            setReloadKey((k) => k + 1);

            // Fechar dialog e limpar estados
            setOpenDialogReverseSale(false);
            setSelectedSaleForReverse(null);

            setSnackbarMessage('Venda estornada com sucesso!');
            setSnackbarSeverity('success');
            setOpenSnackbar(true);
        } catch (error) {
            console.error('Erro ao estornar venda:', error);

            setSnackbarMessage(error?.response?.data?.message || 'Erro ao estornar venda.');
            setSnackbarSeverity('error');
            setOpenSnackbar(true);
        } finally {
            setLoadingReverse(false);
        }
    }

    const columns = [
        {
            path: 'number',
            name: 'Número',
            invisible: false,
            render: (row) => (
                <Typography component="span" sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>
                    {row.number}
                </Typography>
            ),
        },
        {
            path: 'created_date',
            name: 'Data',
            invisible: false,
        },
        {
            path: 'customer_name',
            name: 'Cliente',
            invisible: false,
            render: (row) => (
                <Typography component="span" sx={{ fontSize: '13px', color: row.customer_name ? 'text.primary' : 'text.disabled' }}>
                    {row.customer_name || '—'}
                </Typography>
            ),
        },
        {
            path: 'total_amount',
            name: 'Valor Total',
            invisible: false,
            render: (row) => `R$ ${getSaleRowGrossAmount(row)}`,
        },
        {
            path: 'total_item_discount',
            name: 'Desc. Itens',
            invisible: false,
            render: (row) =>
                hasNonZeroProductItemDiscount(row) ? (
                    <Typography component="span" sx={{ fontSize: '13px', color: 'warning.main', fontWeight: 600 }}>
                        − R$ {getSaleRowItemDiscount(row)}
                    </Typography>
                ) : (
                    <Typography component="span" sx={{ color: 'text.disabled' }}>—</Typography>
                ),
        },
        {
            path: 'total_discount',
            name: 'Total c/ Desconto',
            invisible: false,
            render: (row) => (
                <Typography component="span" sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>
                    R$ {getSaleRowFinalTotal(row)}
                </Typography>
            ),
        },
        {
            path: 'status',
            name: 'Status',
            invisible: false,
            render: (row) => <StatusChip domain="sale" value={row.status} />,
        },
    ]

    const actions = [
        {
            icon: <Eye size={16} strokeWidth={2} />,
            props: {
                onClick: (row) => {
                    saveFiltersAndOpenSale(row.id);
                },
                tooltip: 'Visualizar',
                cursor: 'pointer',
                sx: { mr: 1 }
            }
        },
        ...(hasPermission('sale.create') ? [
        {
            icon: <Play size={16} strokeWidth={2} color={theme.palette.primary.main} />,
            props: {
                onClick: (row) => {
                    navigate(`/sale?id=${row.id}`);
                },
                tooltip: 'Continuar venda',
                style: { cursor: 'pointer' }
            },
            ruleShow: 'Iniciada'
        }] : []),
        ...(hasPermission('sale.cancel') ? [
        {
            icon: <Ban size={16} strokeWidth={2} color={theme.palette.error.main} />,
            props: {
                onClick: (row) => {
                    handleCancelSaleClick(row.id);
                },
                tooltip: 'Cancelar venda',
                style: { cursor: 'pointer' }
            },
            ruleShow: 'Iniciada'
        }] : []),
        ...(hasPermission('sale.reverse') ? [
        {
            icon: <Undo2 size={16} strokeWidth={2} color={theme.palette.warning.main} />,
            props: {
                onClick: (row) => {
                    handleReverseSaleClick(row.id);
                },
                tooltip: 'Estornar venda',
                style: { cursor: 'pointer' }
            },
            ruleShow: 'Finalizada'
        }] : []),
    ]

    // Rótulo do chip fixo (`tb-field` — DESIGN-SYSTEM.md §5.5/§9: micro-label
    // uppercase renderizado fora do campo, não o `InputLabel`/floating label
    // padrão do MUI usado nos formulários de inserção).
    const TbLabel = ({ children }) => (
        <Typography sx={{ fontSize: '10.5px', lineHeight: '13px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '6px' }}>
            {children}
        </Typography>
    );

    // Raio do `tb-field` da toolbar de filtro (§5.5 B): 9px, um pouco mais
    // reto que o `ff-input` (12px) aplicado globalmente pelo tema.
    const TB_FIELD_RADIUS = '9px';

    // Período diferente do padrão (mês atual) conta como 1 filtro ativo.
    const defaultPeriod = getCurrentMonthRange();
    const isDefaultPeriod = Boolean(
        appliedPeriod.start && appliedPeriod.end
        && appliedPeriod.start.isSame(defaultPeriod.start, 'day')
        && appliedPeriod.end.isSame(defaultPeriod.end, 'day')
    );

    // Shell mobile: lista em cartões do protótipo `vendas/lista.html` (mesmos dados, filtros e navegação).
    if (isMobileShell) {
        return (
            <PageScaffold
                links={[]}
                allowBleed
                titlePageTarget={'Minhas Vendas'}
                openSnackbar={openSnackbar}
                setOpenSnackbar={setOpenSnackbar}
                message={snackbarMessage}
                severity={snackbarSeverity}
            >
                <MobileSalesList
                    sales={sales}
                    loading={loading}
                    totalCount={totalCount}
                    amountTotal={amountTotal}
                    hasMore={sales.length < totalCount}
                    onLoadMore={() => setMobilePage((current) => current + 1)}
                    status={selectedStatus}
                    onStatusChange={handleChangeStatus}
                    search={search}
                    onSearchChange={handleChangeSearch}
                    appliedPeriod={appliedPeriod}
                    onApplyPeriod={handleApplyMobilePeriod}
                    onClearFilters={handleClearFilters}
                    statusOptions={STATUS_OPTIONS}
                    canCreate={hasPermission('sale.create')}
                    onNew={() => navigate('/sale')}
                    onOpen={saveFiltersAndOpenSale}
                    getFinalTotal={getSaleRowFinalTotal}
                />
            </PageScaffold>
        );
    }

    return (
        <PageScaffold
            links={[]}
            titlePageTarget={'Minhas Vendas'}
            openSnackbar={openSnackbar}
            setOpenSnackbar={setOpenSnackbar}
            message={snackbarMessage}
            severity={snackbarSeverity}
        >
            <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                {/* Page head — ícone + título + descrição + ação primária,
                    igual a `prototipo-lista-vendas.html` (`page-head`/`page-icon`). */}
                <Box sx={{
                    display: 'flex',
                    alignItems: { xs: 'flex-start', sm: 'flex-end' },
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: 3,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 38,
                            height: 38,
                            borderRadius: '11px',
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#fff',
                            background: (t) => t.palette.gradient.brand,
                        }}>
                            <BarChart3 size={18} strokeWidth={2} />
                        </Box>
                        <Box>
                            <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                                Vendas
                            </Typography>
                            <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                                Acompanhe, cancele e estorne as vendas realizadas.
                            </Typography>
                        </Box>
                    </Box>

                    {hasPermission('sale.create') && (
                        <NewItemButton label="Nova venda" onClick={() => navigate('/sale')} />
                    )}
                </Box>

                {/* Toolbar de filtros — período + status + busca (§6.1 `report-toolbar`). */}
                <FilterToolbar
                    plain
                    activeCount={(selectedStatus ? 1 : 0) + (isDefaultPeriod ? 0 : 1)}
                    onClear={handleClearFilters}
                    onApply={handleApplyFilters}
                    primary={(
                        <Box sx={{ flex: '0 0 220px' }}>
                            <TbLabel>Buscar</TbLabel>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Número da venda…"
                                value={search}
                                onChange={(e) => handleChangeSearch(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search size={15} strokeWidth={2} />
                                        </InputAdornment>
                                    ),
                                    sx: { height: 40, fontSize: '13px', borderRadius: TB_FIELD_RADIUS },
                                }}
                            />
                        </Box>
                    )}
                    actions={(
                        <Stack direction="row" spacing={1.25} alignItems="center">
                            <Button
                                variant="text"
                                onClick={handleClearFilters}
                                sx={{ height: 40, px: 1.25, color: 'text.secondary', fontSize: '12.5px', fontWeight: 600, '&:hover': { color: 'primary.main', bgcolor: 'transparent', textDecoration: 'underline' } }}
                            >
                                Limpar
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<Search size={15} strokeWidth={2} />}
                                onClick={handleApplyFilters}
                                sx={{ height: 40, px: 2.25 }}
                            >
                                Buscar
                            </Button>
                        </Stack>
                    )}
                >
                        <Box>
                            <TbLabel>Período</TbLabel>
                            <DesktopPeriodSelector
                                start={selectedDate}
                                end={selectedDateEnd}
                                onChange={(start, end) => {
                                    setSelectedDate(start);
                                    setSelectedDateEnd(end);
                                }}
                                showRefresh={false}
                                showShortcuts={false}
                                dualMonth
                                align="start"
                            />
                        </Box>

                        <Box>
                            <TbLabel>Status</TbLabel>
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                                <Select
                                    value={selectedStatus}
                                    displayEmpty
                                    onChange={(e) => handleChangeStatus(e.target.value)}
                                    renderValue={(value) => value || 'Todos'}
                                    sx={{ height: 40, fontSize: '13px', borderRadius: TB_FIELD_RADIUS }}
                                >
                                    <MenuItem value="">Todos</MenuItem>
                                    {STATUS_OPTIONS.map((statusOption) => (
                                        <MenuItem key={statusOption} value={statusOption}>
                                            {/* <StatusChip domain="sale" value={statusOption} sx={{ mr: 1 }} /> */}
                                            {statusOption}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                </FilterToolbar>

                {/* Chips de filtro ativo + contagem/total — mesma leitura do
                    `chips-row` do protótipo. */}
                <FilterChips>
                    <Chip
                        label={appliedPeriod.start || appliedPeriod.end
                            ? `Período: ${appliedPeriod.start ? appliedPeriod.start.format('DD/MM/YYYY') : '…'} – ${appliedPeriod.end ? appliedPeriod.end.format('DD/MM/YYYY') : '…'}`
                            : 'Período: todas as datas'}
                        size="small"
                        onDelete={appliedPeriod.start || appliedPeriod.end ? handleResetPeriod : undefined}
                        deleteIcon={<X size={12} strokeWidth={2.5} />}
                        sx={{ bgcolor: 'info.bg', color: 'primary.main' }}
                    />
                    {selectedStatus && (
                        <Chip
                            label={`Status: ${selectedStatus}`}
                            size="small"
                            onDelete={() => handleChangeStatus('')}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                        />
                    )}
                    {search && (
                        <Chip
                            label={`Busca: "${search}"`}
                            size="small"
                            onDelete={() => handleChangeSearch('')}
                            deleteIcon={<X size={12} strokeWidth={2.5} />}
                            sx={{ bgcolor: 'background.subtle', color: 'text.secondary' }}
                        />
                    )}
                    <Chip
                        label={`${totalCount} venda${totalCount === 1 ? '' : 's'} encontrada${totalCount === 1 ? '' : 's'}`}
                        size="small"
                        sx={{ bgcolor: 'background.subtle', color: 'text.secondary'}}
                    />
                    {/* Total de vendas: calculado no servidor (`amount`) sobre
                        todas as vendas do filtro (período + status + busca,
                        não só a página), com a fórmula da coluna "Total c/
                        Desconto". Sem filtro de status, canceladas/estornadas
                        ficam fora da soma. */}
                    <Chip
                        
                        label={`Total de vendas: R$ ${amountTotal || '0,00'}`}
                        size="small"
                        sx={{ bgcolor: 'info.bg', color: 'primary.main', fontSize: '1rem', fontWeight: 600 }}
                    />
                    {/* <Chip
                        label={`Total c/ desconto: R$ ${amountTotal || '0,00'}`}
                        size="small"
                        sx={{ bgcolor: 'success.bg', color: 'success.main' }}
                    /> */}
                </FilterChips>

                <DefaultTable
                    colunms={columns}
                    data={sales}
                    iconsActions={actions}
                    loading={loading}
                    onRowClick={(row) => saveFiltersAndOpenSale(row.id)}
                    rowPerPage={pageSize}
                    rowsPerPageOptions={[5, 8, 15, 25, 50]}
                    itemLabel="vendas"
                    serverPagination={{
                        page,
                        rowsPerPage: pageSize,
                        total: totalCount,
                        onPageChange: setPage,
                        onRowsPerPageChange: handlePageSizeChange,
                    }}
                />
            </Box>

            {/* Dialog para cancelamento de venda */}
            <DialogForm
                iconType="warning"
                title="Cancelar Venda"
                content="Deseja realmente cancelar esta venda? Informe o motivo do cancelamento."
                onSubmit={handleConfirmCancelSale}
                open={openDialogCancelSale}
                onClose={() => {
                    setOpenDialogCancelSale(false);
                    setSelectedSaleForCancel(null);
                    setCancelReason('');
                }}
                confirmButtonText="Confirmar Cancelamento"
                cancelButtonText="Cancelar"
                loading={loadingCancel}
                loadingText="Processando cancelamento..."
            >
                <TextField
                    label="Motivo do Cancelamento"
                    fullWidth
                    multiline
                    rows={3}
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    placeholder="Digite o motivo do cancelamento..."
                    required
                    sx={{ mt: 2 }}
                />
            </DialogForm>

            {/* Dialog para estorno de venda */}
            <DialogForm
                iconType="warning"
                title="Estornar Venda"
                content="Deseja realmente estornar esta venda? Esta ação não pode ser desfeita."
                onSubmit={handleConfirmReverseSale}
                open={openDialogReverseSale}
                onClose={() => {
                    setOpenDialogReverseSale(false);
                    setSelectedSaleForReverse(null);
                }}
                confirmButtonText="Confirmar Estorno"
                cancelButtonText="Cancelar"
                loading={loadingReverse}
                loadingText="Processando estorno..."
            />
        </PageScaffold>
    )
}
